import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { ThemeProvider } from '@/providers/ThemeProvider'
import { AppLayout } from '@/components/layout/AppLayout'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { PageLoader } from '@/components/ui/PageLoader'
import { Toaster } from '@/components/ui/Toaster'

// ── Auth pages ─────────────────────────────────────────────────────────────────
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'))
const TwoFactorVerifyPage = lazy(() => import('@/pages/auth/TwoFactorVerifyPage'))

// ── App pages (lazy-loaded for code splitting) ────────────────────────────────
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const LiveMapPage   = lazy(() => import('@/pages/LiveMapPage'))

// Tracking
const VehiclesPage = lazy(() => import('@/pages/tracking/VehiclesPage'))
const DevicesPage = lazy(() => import('@/pages/tracking/DevicesPage'))
const SimsPage = lazy(() => import('@/pages/tracking/SimsPage'))
const DriversPage = lazy(() => import('@/pages/tracking/DriversPage'))
const GeofencesPage = lazy(() => import('@/pages/tracking/GeofencesPage'))
const TripsPage = lazy(() => import('@/pages/tracking/TripsPage'))
const TripDetailPage = lazy(() => import('@/pages/tracking/TripDetailPage'))
const AlertsPage = lazy(() => import('@/pages/tracking/AlertsPage'))
const PacketInspectorPage = lazy(() => import('@/pages/tracking/PacketInspectorPage'))
const GpsHistoryPage = lazy(() => import('@/pages/tracking/GpsHistoryPage'))
const HistoryPage = lazy(() => import('@/pages/tracking/HistoryPage'))

// School
const SchoolsPage = lazy(() => import('@/pages/school/SchoolsPage'))
const SchoolRoutesPage = lazy(() => import('@/pages/school/RoutesPage'))
const StudentsPage = lazy(() => import('@/pages/school/StudentsPage'))
const SchoolAttendancePage = lazy(() => import('@/pages/school/AttendancePage'))
const SchoolRunsPage = lazy(() => import('@/pages/school/RunsPage'))
const SchoolSchedulePage = lazy(() => import('@/pages/school/SchedulePage'))
const SchoolStaffPage = lazy(() => import('@/pages/school/StaffPage'))
const SchoolMembersPage = lazy(() => import('@/pages/school/MembersPage'))
const ParentPortalPage = lazy(() => import('@/pages/school/ParentPortalPage'))
const DriverPage = lazy(() => import('@/pages/school/DriverPage'))

// Field Force
const EmployeesPage = lazy(() => import('@/pages/fieldforce/EmployeesPage'))
const FieldAttendancePage = lazy(() => import('@/pages/fieldforce/AttendancePage'))
const TasksPage = lazy(() => import('@/pages/fieldforce/TasksPage'))

// Pets
const PetsPage = lazy(() => import('@/pages/PetsPage'))

// EV
const BatteriesPage        = lazy(() => import('@/pages/ev/BatteriesPage'))
const BatterySwapsPage     = lazy(() => import('@/pages/ev/BatterySwapsPage'))
const EVStationsPage       = lazy(() => import('@/pages/ev/StationsPage'))
const EvStatusPage         = lazy(() => import('@/pages/ev/EvStatusPage'))
const EvHistoryPage        = lazy(() => import('@/pages/ev/EvHistoryPage'))
const ChargingSessionsPage = lazy(() => import('@/pages/ev/ChargingSessionsPage'))

// Dispatch
const BookingsPage = lazy(() => import('@/pages/dispatch/BookingsPage'))
const DispatchClientsPage = lazy(() => import('@/pages/dispatch/ClientsPage'))
const ServiceTypesPage = lazy(() => import('@/pages/dispatch/ServiceTypesPage'))
const PromosPage = lazy(() => import('@/pages/dispatch/PromosPage'))
const PackagesPage = lazy(() => import('@/pages/dispatch/PackagesPage'))

// Billing
const BillingDashboardPage = lazy(() => import('@/pages/billing/BillingDashboardPage'))
const SubscriptionsPage   = lazy(() => import('@/pages/billing/SubscriptionsPage'))
const MySubscriptionPage  = lazy(() => import('@/pages/billing/MySubscriptionPage'))
const InvoicesPage        = lazy(() => import('@/pages/billing/InvoicesPage'))
const PaymentsWalletPage  = lazy(() => import('@/pages/billing/PaymentsWalletPage'))
const PlansPage           = lazy(() => import('@/pages/billing/PlansPage'))
const DomainPlansPage     = lazy(() => import('@/pages/billing/DomainPlansPage'))
const PlatformPlanPage    = lazy(() => import('@/pages/billing/PlatformPlanPage'))
const GatewaysPage        = lazy(() => import('@/pages/billing/GatewaysPage'))
const BillingProfilePage  = lazy(() => import('@/pages/billing/BillingProfilePage'))
const PdfTemplatesPage       = lazy(() => import('@/pages/billing/PdfTemplatesPage'))
const TaxConfigPage          = lazy(() => import('@/pages/billing/TaxConfigPage'))
const SigningCertPage        = lazy(() => import('@/pages/billing/SigningCertPage'))
const PlatformProfilePage    = lazy(() => import('@/pages/billing/PlatformProfilePage'))
const PlatformGatewaysPage      = lazy(() => import('@/pages/billing/PlatformGatewaysPage'))
const CustomerBillingPage    = lazy(() => import('@/pages/billing/CustomerBillingPage'))
const MyBillingPage          = lazy(() => import('@/pages/billing/MyBillingPage'))
const DunningRulesPage       = lazy(() => import('@/pages/billing/DunningRulesPage'))
const UpcomingRenewalsPage   = lazy(() => import('@/pages/billing/UpcomingRenewalsPage'))
const OverdueAccountsPage    = lazy(() => import('@/pages/billing/OverdueAccountsPage'))

// Reports
const ReportsPage          = lazy(() => import('@/pages/ReportsPage'))
const GeofenceEventsPage   = lazy(() => import('@/pages/GeofenceEventsPage'))

// Scoring
const ScoringPage = lazy(() => import('@/pages/ScoringPage'))

// Admin
const AdminUsersPage = lazy(() => import('@/pages/admin/UsersPage'))
const UserTypesPage = lazy(() => import('@/pages/admin/UserTypesPage'))
const AlertTypesPage = lazy(() => import('@/pages/admin/AlertTypesPage'))
const AlertRulesPage = lazy(() => import('@/pages/admin/AlertRulesPage'))
const NotificationsPage = lazy(() => import('@/pages/admin/NotificationsPage'))
const PaymentGatewaysPage = lazy(() => import('@/pages/admin/PaymentGatewaysPage'))
const DomainSettingsPage = lazy(() => import('@/pages/admin/DomainSettingsPage'))
const VehicleTypesPage = lazy(() => import('@/pages/admin/VehicleTypesPage'))
const DataBrokerPage = lazy(() => import('@/pages/admin/DataBrokerPage'))
const ApiKeysPage            = lazy(() => import('@/pages/admin/ApiKeysPage'))
const DashcamConfigPage      = lazy(() => import('@/pages/admin/DashcamConfigPage'))
const DashcamPage            = lazy(() => import('@/pages/admin/DashcamPage'))
const DocumentRemindersPage  = lazy(() => import('@/pages/admin/DocumentRemindersPage'))
const BulkImportPage         = lazy(() => import('@/pages/admin/BulkImportPage'))
const OnboardingPage         = lazy(() => import('@/pages/admin/OnboardingPage'))
const AuditLogPage           = lazy(() => import('@/pages/admin/AuditLogPage'))
const DelegationsPage        = lazy(() => import('@/pages/admin/DelegationsPage'))
const AnnouncementsPage      = lazy(() => import('@/pages/admin/AnnouncementsPage'))
const CalendarsPage          = lazy(() => import('@/pages/CalendarsPage'))
const MySettingsPage         = lazy(() => import('@/pages/settings/MySettingsPage'))

// Transport
const TransportVendorsPage   = lazy(() => import('@/pages/transport/VendorsPage'))
const TransportEmployeesPage = lazy(() => import('@/pages/transport/EmployeesPage'))
const TransportRoutesPage    = lazy(() => import('@/pages/transport/RoutesPage'))
const TransportRostersPage   = lazy(() => import('@/pages/transport/RostersPage'))
const TransportSOSPage       = lazy(() => import('@/pages/transport/SOSPage'))
const TransportVRPPage       = lazy(() => import('@/pages/transport/VRPPage'))

// SOS (standalone vehicle/panic-button module — issue #89)
const SOSActivePage  = lazy(() => import('@/pages/sos/SOSPage').then(m => ({ default: m.SOSActivePage })))
const SOSHistoryPage = lazy(() => import('@/pages/sos/SOSPage').then(m => ({ default: m.SOSHistoryPage })))

// White Label
const WhiteLabelDomainsPage     = lazy(() => import('@/pages/whitelabel/DomainsPage'))
const WhiteLabelMigrationsPage  = lazy(() => import('@/pages/whitelabel/MigrationsPage'))
const SimulatePage              = lazy(() => import('@/pages/whitelabel/SimulatePage'))

// AI Assistant
const AssistantPage       = lazy(() => import('@/pages/AssistantPage'))
const AssistantConfigPage = lazy(() => import('@/pages/admin/AssistantConfigPage'))

// Maintenance
const ServiceRecordsPage   = lazy(() => import('@/pages/maintenance/ServiceRecordsPage'))
const ServiceSchedulesPage = lazy(() => import('@/pages/maintenance/ServiceSchedulesPage'))

// Compliance / E-Challans
const ChallansPage      = lazy(() => import('@/pages/compliance/ChallansPage'))
const ChallanConfigPage = lazy(() => import('@/pages/admin/ChallanConfigPage'))

// Routing / Route Planning
const RoutingPage = lazy(() => import('@/pages/routing/RoutingPage'))

// Haul Cycles
const HaulCyclesPage = lazy(() => import('@/pages/haulcycles/HaulCyclesPage'))

// Technician
const StockPage    = lazy(() => import('@/pages/technician/StockPage'))
const ActivatePage = lazy(() => import('@/pages/technician/ActivatePage'))
const HandoverPage = lazy(() => import('@/pages/technician/HandoverPage'))

// System (super admin)
const SystemPage = lazy(() => import('@/pages/system/SystemPage'))
const ParsersPage = lazy(() => import('@/pages/system/ParsersPage'))

// Profile
// ProfilePage merged into DomainSettingsPage

// Public (no auth required)
const SharedLocationPage = lazy(() => import('@/pages/SharedLocationPage'))
const PublicSchoolSharePage = lazy(() => import('@/pages/school/PublicSchoolSharePage'))
const PetLiveTrackingPage = lazy(() => import('@/pages/PetLiveTrackingPage'))

/** Admin/Super Admin → Dashboard, parent-only → Parent Portal, everyone else → Live Map */
function HomeRedirect() {
  const user       = useAuthStore((s) => s.user)
  const is_super   = useAuthStore((s) => s.is_super)
  const permMap    = useAuthStore((s) => s.permissions)

  // While profile is still loading, fall through to map
  if (!user) return <Navigate to="/map" replace />

  const typeName = (user.user_type?.name ?? '').toLowerCase()
  const isAdmin  = is_super || user.is_admin || typeName.includes('admin')
  if (isAdmin) return <DashboardPage />

  // Parent-only: has school canRead but no canCreate/canUpdate and no vehicle tracking perms
  // school:parent slug → canRead=true, canCreate=false, canUpdate=false
  const schoolReadOnly = permMap['school']?.canRead === true &&
    !permMap['school']?.canCreate && !permMap['school']?.canUpdate
  const hasTracking = permMap['tracking']?.canRead === true ||
    permMap['vehicles']?.canRead === true ||
    permMap['map']?.canRead === true

  if (schoolReadOnly && !hasTracking) return <Navigate to="/school/parent-portal" replace />

  // Owner, Viewer, User, Sub-user, etc. → Live Map
  return <Navigate to="/map" replace />
}

export default function App() {
  return (
    <ThemeProvider>
      <Toaster />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ── Public routes (no auth, no sidebar) ─────────────────── */}
            <Route path="/share/:token" element={<SharedLocationPage />} />
            <Route path="/shared/:token" element={<SharedLocationPage />} />
            <Route path="/school-share/:token" element={<PublicSchoolSharePage />} />
            <Route path="/live/:token" element={<PetLiveTrackingPage />} />

            {/* ── Auth routes (no sidebar) ─────────────────────────────── */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/login/2fa" element={<TwoFactorVerifyPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
            </Route>

            {/* ── Authenticated app routes ─────────────────────────────── */}
            <Route element={<AppLayout />}>
              <Route index element={<HomeRedirect />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/map"      element={<LiveMapPage />} />
              <Route path="/testlive" element={<LiveMapPage />} />

              {/* Tracking */}
              <Route path="/vehicles" element={<VehiclesPage />} />
              <Route path="/vehicles/:id/history" element={<GpsHistoryPage />} />
              <Route path="/devices" element={<DevicesPage />} />
              <Route path="/sims" element={<SimsPage />} />
              <Route path="/drivers" element={<DriversPage />} />
              <Route path="/geofences" element={<GeofencesPage />} />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/trips" element={<TripsPage />} />
              <Route path="/trips/:id" element={<TripDetailPage />} />
              <Route path="/alerts" element={<AlertsPage />} />
              <Route path="/packets" element={<PacketInspectorPage />} />

              {/* School */}
              <Route path="/school/schools" element={<SchoolsPage />} />
              <Route path="/school/routes" element={<SchoolRoutesPage />} />
              <Route path="/school/students" element={<StudentsPage />} />
              <Route path="/school/attendance" element={<SchoolAttendancePage />} />
              <Route path="/school/runs" element={<SchoolRunsPage />} />
              <Route path="/school/schedule" element={<SchoolSchedulePage />} />
              <Route path="/school/staff" element={<SchoolStaffPage />} />
              <Route path="/school/members" element={<SchoolMembersPage />} />
              <Route path="/school/driver" element={<DriverPage />} />
              <Route path="/school/parent-portal" element={<ParentPortalPage />} />

              {/* Field Force */}
              <Route path="/field-force/employees" element={<EmployeesPage />} />
              <Route path="/field-force/attendance" element={<FieldAttendancePage />} />
              <Route path="/field-force/tasks" element={<TasksPage />} />

              {/* Pets */}
              <Route path="/pets" element={<PetsPage />} />

              {/* EV */}
              <Route path="/ev/batteries"          element={<BatteriesPage />} />
              <Route path="/ev/swaps"              element={<BatterySwapsPage />} />
              <Route path="/ev/stations"           element={<EVStationsPage />} />
              <Route path="/ev/status"             element={<EvStatusPage />} />
              <Route path="/ev/history"            element={<EvHistoryPage />} />
              <Route path="/ev/charging-sessions"  element={<ChargingSessionsPage />} />

              {/* Dispatch */}
              <Route path="/dispatch/bookings" element={<BookingsPage />} />
              <Route path="/dispatch/clients" element={<DispatchClientsPage />} />
              <Route path="/dispatch/service-types" element={<ServiceTypesPage />} />
              <Route path="/dispatch/promos" element={<PromosPage />} />
              <Route path="/dispatch/packages" element={<PackagesPage />} />

              {/* Billing */}
              <Route path="/billing/dashboard"        element={<BillingDashboardPage />} />
              <Route path="/billing/subscriptions"    element={<SubscriptionsPage />} />
              <Route path="/billing/my-subscription"  element={<MySubscriptionPage />} />
              <Route path="/billing/invoices"      element={<InvoicesPage />} />
              <Route path="/billing/payments"      element={<PaymentsWalletPage />} />
              <Route path="/billing/wallet"        element={<PaymentsWalletPage />} />
              <Route path="/billing/plans"         element={<PlansPage />} />
              <Route path="/billing/domain-plans"  element={<DomainPlansPage />} />
              <Route path="/billing/platform-plan"  element={<PlatformPlanPage />} />
              <Route path="/billing/gateways"      element={<GatewaysPage />} />
              <Route path="/billing/profile"       element={<BillingProfilePage />} />
              <Route path="/billing/pdf-templates"     element={<PdfTemplatesPage />} />
              <Route path="/billing/tax-config"        element={<TaxConfigPage />} />
              <Route path="/billing/signing-cert"      element={<SigningCertPage />} />
              <Route path="/billing/platform-profile"  element={<PlatformProfilePage />} />
              <Route path="/billing/platform-gateways"      element={<PlatformGatewaysPage />} />
              <Route path="/billing/platform-pdf-templates" element={<PdfTemplatesPage />} />
              <Route path="/billing/customers"         element={<CustomerBillingPage />} />
              <Route path="/billing/my-billing"        element={<MyBillingPage />} />
              <Route path="/billing/dunning-rules"    element={<DunningRulesPage />} />
              <Route path="/billing/renewals"          element={<UpcomingRenewalsPage />} />
              <Route path="/billing/overdue"           element={<OverdueAccountsPage />} />

              {/* Reports */}
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/reports/geofence-events" element={<GeofenceEventsPage />} />

              {/* Scoring */}
              <Route path="/scoring" element={<ScoringPage />} />

              {/* Admin */}
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/user-types" element={<UserTypesPage />} />
              <Route path="/admin/alert-types" element={<AlertTypesPage />} />
              <Route path="/admin/alert-rules" element={<AlertRulesPage />} />
              <Route path="/admin/notifications" element={<NotificationsPage />} />
              <Route path="/admin/payment-gateways" element={<PaymentGatewaysPage />} />
              <Route path="/admin/domain" element={<DomainSettingsPage />} />
              <Route path="/admin/vehicle-types" element={<VehicleTypesPage />} />
              <Route path="/admin/data-brokers" element={<DataBrokerPage />} />
              <Route path="/admin/api-keys"           element={<ApiKeysPage />} />
              <Route path="/admin/dashcam-config"     element={<DashcamConfigPage />} />
              <Route path="/dashcam"                   element={<DashcamPage />} />
              <Route path="/admin/document-reminders" element={<DocumentRemindersPage />} />
              <Route path="/admin/bulk-import"       element={<BulkImportPage />} />
              <Route path="/admin/onboarding"        element={<OnboardingPage />} />
              <Route path="/admin/audit-log"         element={<AuditLogPage />} />
              <Route path="/admin/delegations"      element={<DelegationsPage />} />
              <Route path="/admin/calendars"          element={<CalendarsPage />} />
              <Route path="/admin/announcements"      element={<AnnouncementsPage />} />

              {/* Transport */}
              <Route path="/transport/vendors"   element={<TransportVendorsPage />} />
              <Route path="/transport/employees" element={<TransportEmployeesPage />} />
              <Route path="/transport/routes"    element={<TransportRoutesPage />} />
              <Route path="/transport/rosters"   element={<TransportRostersPage />} />
              <Route path="/transport/sos"       element={<TransportSOSPage />} />
              <Route path="/transport/vrp"       element={<TransportVRPPage />} />

              {/* Standalone SOS module (vehicle/panic-button) — frontend #89 */}
              <Route path="/sos/active"     element={<SOSActivePage />} />
              <Route path="/sos/active/:id" element={<SOSActivePage />} />
              <Route path="/sos/history"    element={<SOSHistoryPage />} />

              {/* White Label */}
              <Route path="/white-label/domains"     element={<WhiteLabelDomainsPage />} />
              <Route path="/white-label/migrations"  element={<WhiteLabelMigrationsPage />} />
              <Route path="/white-label/simulate"    element={<SimulatePage />} />

              {/* AI Assistant */}
              <Route path="/assistant"               element={<AssistantPage />} />
              <Route path="/admin/assistant-config"  element={<AssistantConfigPage />} />

              {/* Compliance / E-Challans */}
              <Route path="/compliance/challans"   element={<ChallansPage />} />
              <Route path="/admin/challans"        element={<ChallanConfigPage />} />

              {/* Maintenance */}
              <Route path="/maintenance/records"   element={<ServiceRecordsPage />} />
              <Route path="/maintenance/schedules" element={<ServiceSchedulesPage />} />

              {/* Routing / Route Planning */}
              <Route path="/routing" element={<RoutingPage />} />

              {/* Haul Cycles */}
              <Route path="/haul-cycles" element={<HaulCyclesPage />} />

              {/* Technician */}
              <Route path="/technician/stock"    element={<StockPage />} />
              <Route path="/technician/activate" element={<ActivatePage />} />
              <Route path="/technician/handover" element={<HandoverPage />} />

              {/* System */}
              <Route path="/system" element={<SystemPage />} />
              <Route path="/system/parsers" element={<ParsersPage />} />

              {/* Settings */}
              <Route path="/settings/me" element={<MySettingsPage />} />
              <Route path="/settings/domain" element={<DomainSettingsPage />} />
              <Route path="/profile" element={<MySettingsPage />} />

              {/* Catch-all → dashboard */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ThemeProvider>
  )
}
